import classnames from 'classnames';
import React, { useState } from 'react';
import {
  Area,
  CartesianGrid,
  Legend,
  AreaChart as ReAreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GlobalProps from '../globalProps';
import { CustomToolTip } from './CustomToolTip';
import { ChartLegend } from './LineAreaChart/ChartLegend';

import { formatDateTimeWithoutYear, formatDateWithoutYear } from '../../util/formatters';
import './AreaChart.css';

export interface Dataset {
  title: string;
  values: number[];
  color?: string;
}

interface Props extends GlobalProps {
  datasets: Dataset[];
  labels: string[];
  tooltipTitle?: (index: number) => string;
  formatTooltipValues?: 'currency';
  showTimeOnXAxis?: boolean;
}

function convertedData(data: Dataset[], labels: string[]): Array<{ label: string }> {
  return labels.map((label, labelIndex) => {
    return {
      label,
      ...data.reduce((carry, dataSet, dataSetIndex) => {
        carry[dataSetIndex] = dataSet.values[labelIndex];
        return carry;
      }, {}),
    };
  });
}

export function AreaChart({
  datasets,
  labels,
  tooltipTitle,
  className,
  style,
  formatTooltipValues,
  showTimeOnXAxis,
}: Props): JSX.Element {
  const [longestTick, setLongestTick] = useState('');
  const yTickFormatter = (t: number): string => {
    const formattedTick = t === 0 ? '' : t >= 1000 ? `${Math.round(t / 1000)}k` : String(t);

    if (longestTick.length < formattedTick.length) {
      setLongestTick(formattedTick);
    }
    return formattedTick;
  };
  const getYAxisTickWidth = (): number => {
    const charWidth = 8;
    return longestTick.length * charWidth + 15;
  };
  return (
    <div className={classnames('area-chart', className)} style={style}>
      <ResponsiveContainer width="100%" height="100%">
        <ReAreaChart data={convertedData(datasets, labels)} margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            stroke="#919191"
            dataKey="label"
            tickFormatter={(t) => {
              const date = new Date(t);

              if (showTimeOnXAxis) {
                return formatDateTimeWithoutYear(date) || '';
              }

              return formatDateWithoutYear(date) || '';
            }}
            tickLine={false}
          />
          <YAxis stroke="#919191" width={getYAxisTickWidth()} tickLine={false} tickFormatter={yTickFormatter} />
          <CartesianGrid stroke="#E7E7E7" />
          <Tooltip
            content={
              <CustomToolTip
                dataSets={datasets}
                labels={labels}
                title={tooltipTitle}
                formatValues={formatTooltipValues}
                showHour={showTimeOnXAxis}
              />
            }
          />
          <Tooltip />
          {datasets.map((dataset, index) => (
            <Area
              key={dataset.title}
              type="monotone"
              dataKey={index}
              stroke={dataset.color}
              name={dataset.title}
              fillOpacity={0.5}
              fill={dataset.color}
            />
          ))}

          <Legend content={<ChartLegend labels={datasets.map((d) => d.title)} />} />
        </ReAreaChart>
      </ResponsiveContainer>
    </div>
  );
}
